import { useMemo } from 'react';
import { useApiQuery } from '../api/hooks';
import { Avatar, EmptyState, ErrorBanner, Skeleton } from '../design/primitives/Misc';
import { Icon, type IconName } from '../design/icons';
import { formatDateTime } from '../../lib/format';

// ActivityFeed: GET /v1/activity?entity_type=&entity_id= (blueprint §5.3 activity_log).
// Verbs render as a compact timeline; diffs expand inline.

export interface ActivityEntry {
  id: string;
  verb: string;
  actor_user_id?: string | null;
  actor_name?: string | null;
  entity_type: string;
  entity_id: string;
  diff?: Record<string, { from?: unknown; to?: unknown } | [unknown, unknown]> | null;
  at: string;
}

const VERB_ICON: Record<string, IconName> = { create: 'plus', created: 'plus', update: 'pencil', updated: 'pencil', delete: 'trash', deleted: 'trash', comment: 'message-square', note: 'sticky-note', attach: 'paperclip' };

function verbIcon(verb: string): IconName {
  const last = verb.split('.').pop() ?? verb;
  return VERB_ICON[last] ?? 'activity';
}

function fmt(v: unknown): string {
  if (v == null || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export function ActivityFeed({ entityType, entityId, limit = 50 }: { entityType: string; entityId: string; limit?: number }) {
  const q = useApiQuery<{ data: ActivityEntry[] }>(['activity', entityType, entityId], '/v1/activity', { query: { entity_type: entityType, entity_id: entityId, limit } });
  const rows = useMemo(() => q.data?.data ?? [], [q.data]);
  if (q.isLoading) return <Skeleton lines={4} />;
  if (q.error) return <ErrorBanner error={q.error} />;
  if (rows.length === 0) return <EmptyState title="No activity yet" hint="Changes to this record will appear here." icon="activity" />;
  return (
    <ol className="hf-feed" aria-label="Activity">
      {rows.map((a) => {
        const diff = a.diff ? Object.entries(a.diff) : [];
        return (
          <li key={a.id} className="hf-feed-item">
            <span className="glyph">
              <Icon name={verbIcon(a.verb)} size={13} />
            </span>
            <div className="body">
              <div className="line">
                <Avatar name={a.actor_name ?? a.actor_user_id ?? 'System'} size="sm" />
                <b>{a.actor_name ?? (a.actor_user_id ? a.actor_user_id.slice(0, 8) : 'System')}</b>
                <span className="hf-dim">{a.verb.replace(/[._]/g, ' ')}</span>
                <time className="hf-muted hf-num" dateTime={a.at} title={a.at}>
                  {formatDateTime(a.at)}
                </time>
              </div>
              {diff.length > 0 && (
                <dl className="diff">
                  {diff.map(([k, v]) => {
                    const from = Array.isArray(v) ? v[0] : v?.from;
                    const to = Array.isArray(v) ? v[1] : v?.to;
                    return (
                      <div key={k}>
                        <dt>{k.replace(/_/g, ' ')}</dt>
                        <dd>
                          <s className="hf-muted">{fmt(from)}</s> <Icon name="arrow-right" size={11} /> <span>{fmt(to)}</span>
                        </dd>
                      </div>
                    );
                  })}
                </dl>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
